/**
 * SPA-internal extension registry.
 *
 * Maps slot names → fragment components. Iterated by the `<Slot>` component
 * at render time. Adding a new fragment is a one-line append here; the
 * core pages don't change.
 *
 * Today's registry is hand-maintained because all the fragments are
 * first-party (in this SPA bundle). When a third-party plugin needs to
 * inject UI, this file becomes the integration point — it can either be
 * extended at build time (codegen against `plugins.config.ts`) or at
 * runtime (a dynamic-import loader populates it). Neither matters until
 * we have a second-party consumer.
 */
import type { Extension, SlotName } from './types.js';
import { OrgApplicationsCard } from './extensions/OrgApplicationsCard.js';

/**
 * Internal storage. Exported via `extensionsForSlot()` so consumers don't
 * mutate the array directly.
 */
const EXTENSIONS: Extension[] = [
  {
    id: 'applications.OrgApplicationsList',
    slot: 'org.detail.main.bottom',
    component: OrgApplicationsCard as Extension['component'],
    // No RBAC gate today — the page itself requires org-admin to reach, and
    // the underlying API call enforces permission on the result set. When
    // the apps plugin adds a `applications.read` permission we'll switch
    // to it here.
  },
];

/**
 * Return all extensions registered against the given slot, sorted by
 * `order` (default 100). Stable across renders so React doesn't reshuffle.
 */
export function extensionsForSlot(slot: SlotName): Extension[] {
  return EXTENSIONS.filter((e) => e.slot === slot).sort(
    (a, b) => (a.order ?? 100) - (b.order ?? 100),
  );
}
